import React from 'react';
import { Box, Typography, Button, ButtonGroup, Paper, FormControlLabel, Switch, useTheme } from '@mui/material';
import { AlignHorizontalLeft, AlignHorizontalCenter, AlignHorizontalRight, AlignVerticalTop, AlignVerticalCenter, AlignVerticalBottom } from '@mui/icons-material';
import { usePhotoAddsStore } from '../usePhotoAddsStoreBridge';
import { enableGuides, disableGuides } from '../services/canvasService';

const AlignmentPanel = () => {
  const theme = useTheme();
  const { canvas } = usePhotoAddsStore();
  const [showGuides, setShowGuides] = React.useState(false);

  // Re-apply guides when toggled or when canvas instance becomes available
  React.useEffect(() => {
    if (!canvas) return;
    if (showGuides) {
      // Bright teal, slightly thicker line for visibility on dark canvas
      enableGuides(canvas, { showCenter: true, showThirds: false, color: theme.palette.primary.main, lineWidth: 1.5 });
    } else {
      disableGuides(canvas);
    }
  }, [canvas, showGuides]);

  // Alignment functions using fabric.js
  const alignLeft = () => {
    const activeObject = canvas?.getActiveObject();
    if (activeObject) {
      const bound = activeObject.getBoundingRect(true);
      // Shift object so its bounding left touches canvas left (x=0)
      const newLeft = activeObject.left - bound.left;
      activeObject.set({ left: newLeft });
      activeObject.setCoords();
      canvas.requestRenderAll();
    }
  };

  const alignCenter = () => {
    const activeObject = canvas?.getActiveObject();
    if (activeObject) {
      if (canvas.centerObjectH) {
        canvas.centerObjectH(activeObject);
      } else if (canvas.centerObject) {
        canvas.centerObject(activeObject);
      } else {
        const bound = activeObject.getBoundingRect(true);
        const dx = (canvas.getWidth() - bound.width) / 2 - bound.left;
        activeObject.set({ left: activeObject.left + dx });
      }
      activeObject.setCoords();
      canvas.requestRenderAll();
    }
  };

  const alignRight = () => {
    const activeObject = canvas?.getActiveObject();
    if (activeObject) {
      const bound = activeObject.getBoundingRect(true);
      // Move so bounding right matches canvas width
      const dx = canvas.getWidth() - (bound.left + bound.width);
      activeObject.set({ left: activeObject.left + dx });
      activeObject.setCoords();
      canvas.requestRenderAll();
    }
  };

  const alignTop = () => {
    const activeObject = canvas?.getActiveObject();
    if (activeObject) {
      const bound = activeObject.getBoundingRect(true);
      const newTop = activeObject.top - bound.top;
      activeObject.set({ top: newTop });
      activeObject.setCoords();
      canvas.requestRenderAll();
    }
  };

  const alignMiddle = () => {
    const activeObject = canvas?.getActiveObject();
    if (activeObject) {
      if (canvas.centerObjectV) {
        canvas.centerObjectV(activeObject);
      } else if (canvas.centerObject) {
        canvas.centerObject(activeObject);
      } else {
        const bound = activeObject.getBoundingRect(true);
        const dy = (canvas.getHeight() - bound.height) / 2 - bound.top;
        activeObject.set({ top: activeObject.top + dy });
      }
      activeObject.setCoords();
      canvas.requestRenderAll();
    }
  };

  const alignBottom = () => {
    const activeObject = canvas?.getActiveObject();
    if (activeObject) {
      const bound = activeObject.getBoundingRect(true);
      const dy = canvas.getHeight() - (bound.top + bound.height);
      activeObject.set({ top: activeObject.top + dy });
      activeObject.setCoords();
      canvas.requestRenderAll();
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2 }}>Alignment Tools</Typography>

      <Paper sx={{ p: 2, mb: 2, bgcolor: theme.palette.background.paper }}>
        <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary, mb: 1 }}>Guides</Typography>
        <FormControlLabel
          control={<Switch checked={showGuides} onChange={(e) => setShowGuides(e.target.checked)} />}
          label={<span style={{ color: theme.palette.text.primary }}>Show Guides (non-layer)</span>}
        />
      </Paper>
      
      <Paper sx={{ p: 2, mb: 2, bgcolor: theme.palette.background.paper }}>
        <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary, mb: 1 }}>Horizontal Alignment</Typography>
        <ButtonGroup fullWidth sx={{ mb: 1 }}>
          <Button 
            variant="outlined" 
            startIcon={<AlignHorizontalLeft />} 
            onClick={alignLeft}
            sx={{ color: theme.palette.text.primary, borderColor: theme.palette.divider, '&:hover': { borderColor: theme.palette.action.hover } }}
          >
            Left
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<AlignHorizontalCenter />} 
            onClick={alignCenter}
            sx={{ color: theme.palette.text.primary, borderColor: theme.palette.divider, '&:hover': { borderColor: theme.palette.action.hover } }}
          >
            Center
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<AlignHorizontalRight />} 
            onClick={alignRight}
            sx={{ color: theme.palette.text.primary, borderColor: theme.palette.divider, '&:hover': { borderColor: theme.palette.action.hover } }}
          >
            Right
          </Button>
        </ButtonGroup>
      </Paper>
      
      <Paper sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
        <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary, mb: 1 }}>Vertical Alignment</Typography>
        <ButtonGroup fullWidth>
          <Button 
            variant="outlined" 
            startIcon={<AlignVerticalTop />} 
            onClick={alignTop}
            sx={{ color: theme.palette.text.primary, borderColor: theme.palette.divider, '&:hover': { borderColor: theme.palette.action.hover } }}
          >
            Top
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<AlignVerticalCenter />} 
            onClick={alignMiddle}
            sx={{ color: theme.palette.text.primary, borderColor: theme.palette.divider, '&:hover': { borderColor: theme.palette.action.hover } }}
          >
            Middle
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<AlignVerticalBottom />} 
            onClick={alignBottom}
            sx={{ color: theme.palette.text.primary, borderColor: theme.palette.divider, '&:hover': { borderColor: theme.palette.action.hover } }}
          >
            Bottom
          </Button>
        </ButtonGroup>
      </Paper>
    </Box>
  );
};

export default AlignmentPanel;
